import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { createSmsKeyword, updateSmsKeyword } from 'api/engage';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { MessageNumber, SmsKeyword } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface KeywordFormValues {
    keyword: string;
    response: string;
}

interface SmsKeywordFormModalProps extends AlertDialog.AlertDialogRootProps {
    existingRecord: SmsKeyword | null;
    messageNumber?: MessageNumber;
    onClose?: () => void;
}
const SmsKeywordFormModal = ({
    open,
    onClose,
    existingRecord,
    messageNumber,
    ...restProps
}: SmsKeywordFormModalProps) => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    const methods = Form.useGivebutterForm<KeywordFormValues>({
        defaultValues: {
            keyword: existingRecord?.keyword ?? '',
            response: existingRecord?.response ?? '',
        },
    });
    const { control, handleSubmit, path, setError, watch, resetFormButton } = methods;
    const response = watch('response');

    const { mutate, isPending } = useMutation<SmsKeyword, GivebutterAxiosError, KeywordFormValues>({
        mutationFn: ({ keyword, response }) => {
            const payload = {
                keyword: keyword.toUpperCase(),
                response,
                model: {
                    id: existingRecord?.model?.id.toString() || messageNumber?.id,
                    table: existingRecord?.model_type || 'message_numbers',
                },
            };
            if (existingRecord) {
                return updateSmsKeyword(accountId, existingRecord.id, payload);
            }

            return createSmsKeyword(accountId, payload);
        },
        onSuccess: () => {
            success(`SMS keyword successfully ${existingRecord ? 'updated' : 'created'}.`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageSmsKeywords(accountId),
            });
            onClose?.();
        },
        onError: e => {
            const data = e.response?.data;
            const errors = data?.errors;

            error(data?.message ?? 'Failed to update General Details');
            resetFormButton();

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof KeywordFormValues, {
                        message: errors[field][0],
                    });
                }
            }
        },
    });

    return (
        <AlertDialog.Root
            open={open}
            onOpenChange={isOpen => {
                if (!isOpen) {
                    onClose?.();
                }
            }}
            {...restProps}
        >
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <Form.Provider {...methods}>
                        <form onSubmit={handleSubmit(values => mutate(values))}>
                            <AlertDialog.Body>
                                <AlertDialog.Header>
                                    <p className="text-gray-500">Engage</p>
                                    <AlertDialog.Close />
                                </AlertDialog.Header>
                                <AlertDialog.Title>SMS Keyword</AlertDialog.Title>
                                <AlertDialog.Description>
                                    <div className="flex flex-col gap-4">
                                        <Form.Container>
                                            <Form.Label name={path('keyword')}>Keyword</Form.Label>
                                            <Form.TextInputRoot
                                                control={control}
                                                name="keyword"
                                                rules={{
                                                    required: 'Keyword is required.',
                                                }}
                                            >
                                                <Form.TextInputForm
                                                    autoFocus
                                                    placeholder="E.g. DANCE"
                                                />
                                            </Form.TextInputRoot>
                                            <Form.Error name={path('keyword')} />
                                        </Form.Container>

                                        <Form.Container>
                                            <Form.Label name={path('response')}>
                                                Response
                                            </Form.Label>
                                            <Form.Textarea
                                                control={control}
                                                name="response"
                                                placeholder="Enter a response message here"
                                                className=""
                                                rows={5}
                                                rules={{
                                                    required: 'Response is required.',
                                                    maxLength: {
                                                        value: 140,
                                                        message:
                                                            'Response must be less than 140 characters.',
                                                    },
                                                }}
                                            />
                                            <div className="flex flex-col gap-1">
                                                <p
                                                    className={twMerge(
                                                        'text-xs italic text-gray-600',
                                                        response.length >= 130 &&
                                                            response.length < 140 &&
                                                            'text-orange-600',
                                                        response.length >= 140 && 'text-red-600',
                                                    )}
                                                >
                                                    {response.length} of 140 characters
                                                </p>
                                                <Form.Error name={path('response')} />
                                            </div>
                                        </Form.Container>
                                    </div>
                                </AlertDialog.Description>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <Form.SubmitButton isSubmitting={isPending}>
                                    Submit
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </form>
                    </Form.Provider>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { SmsKeywordFormModal };
